import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useWorkEnvironmentMeasuresQuery } from '../../hooks/queries/useWorkEnvironmentMeasuresQuery';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { ProcessHero } from './components/ProcessHero';
import { ShareCard } from './components/ShareCard';
import { HighlightProgressCard } from './measures/HighlightProgressCard';
import { MeasureAreasCard } from './measures/MeasureAreasCard';
import { MeasuresNextCard } from './measures/MeasuresNextCard';
import { ProposalDialog } from './measures/ProposalDialog';
import { ProposalsCard } from './measures/ProposalsCard';
import { teamLink } from './measuresView';
import { currentStepIndex, daysLeft } from './surveyProcessView';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';

/** Step 5 and 6: the team proposes measures, then points out the ones that matter most */
export const WorkEnvironmentSurveyMeasures = ({ survey }: { survey: WorkEnvironmentSurvey }): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useWorkEnvironmentMeasuresQuery(survey.id, questionLanguageFor(i18n.language));
  const [proposingIn, setProposingIn] = useState<string | null>(null);
  const team = survey.teamName;
  const left = data?.deadline ? daysLeft(data.deadline, new Date()) : null;

  return (
    <div data-size="md">
      <ProcessHero
        title={survey.title}
        intro={t(`workEnvironmentMeasures.intro.${data?.step ?? 'propose'}`, { team })}
        tag={{
          label:
            data && data.step !== 'plan' && left !== null
              ? t(`workEnvironmentMeasures.tag.${data.step}`, { count: left })
              : t('workEnvironmentMeasures.tag.plan'),
          color: 'info',
        }}
        process={{ current: currentStepIndex(survey.status), createdAt: survey.createdAt, responseDeadline: null }}
      />
      <div className={process.layout}>
        <div className={classes.mainColumn}>
          {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
          {isError && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentMeasures.error')}</Paragraph>
            </Alert>
          )}
          {data?.step === 'propose' && <MeasureAreasCard areas={data.areas} onPropose={setProposingIn} />}
          {data && data.step !== 'propose' && (
            <HighlightProgressCard voters={data.voters} expected={data.expected} max={data.maxHighlights} />
          )}
          {data && (
            <ProposalsCard
              surveyId={survey.id}
              step={data.step}
              areas={data.areas}
              proposals={data.proposals}
              onAdd={() => setProposingIn(data.areas[0]?.key ?? null)}
            />
          )}
        </div>
        {data && (
          <div className={process.side}>
            {data.step !== 'plan' && (
              <ShareCard
                link={teamLink(window.location.origin, survey.uuid)}
                text={t(`workEnvironmentMeasures.share.${data.step}`)}
                linkLabel={t(`workEnvironmentMeasures.share.${data.step}Link`)}
                sharing={{
                  surveyId: survey.id,
                  teamId: survey.teamId,
                  kind: data.step === 'propose' ? 'PROPOSALS' : 'HIGHLIGHTS',
                }}
              />
            )}
            <MeasuresNextCard surveyId={survey.id} step={data.step} deadline={data.deadline} />
          </div>
        )}
      </div>
      {data && proposingIn && (
        <ProposalDialog
          surveyId={survey.id}
          areas={data.areas}
          areaKey={proposingIn}
          onClose={() => setProposingIn(null)}
        />
      )}
    </div>
  );
};
